import { formatShortDate } from "@/lib/people-format"
import { personDisplayName, type PersonName } from "@/lib/person-name"
import type {
  TaskAgent,
  TaskAssetKind,
  TaskBoardSearch,
  TaskCardRow,
  TaskKind,
  TaskColumnCursor,
  TaskColumnPage,
  TaskColumnsState,
  TaskPerson,
  TaskPlacement,
  TaskStatus,
} from "@/types/tasks"

export const TASK_STATUSES: readonly TaskStatus[] = ["todo", "in_progress", "review", "done"]

export type TaskColumns = Record<TaskStatus, TaskCardRow[]>

const TASK_PREFIX = "task-"
const COLUMN_PREFIX = "column-"

export function taskDndId(taskId: number): string {
  return `${TASK_PREFIX}${taskId}`
}

export function columnDndId(status: TaskStatus): string {
  return `${COLUMN_PREFIX}${status}`
}

export function taskIdFromDndId(dndId: string): number | null {
  if (!dndId.startsWith(TASK_PREFIX)) return null
  const id = Number(dndId.slice(TASK_PREFIX.length))
  return Number.isInteger(id) ? id : null
}

export function columnStatusFromDndId(dndId: string): TaskStatus | null {
  if (!dndId.startsWith(COLUMN_PREFIX)) return null
  const status = dndId.slice(COLUMN_PREFIX.length)
  return TASK_STATUSES.find((s) => s === status) ?? null
}

function mapColumns(build: (status: TaskStatus) => TaskCardRow[]): TaskColumns {
  return { todo: build("todo"), in_progress: build("in_progress"), review: build("review"), done: build("done") }
}

export function groupTasksByStatus(tasks: TaskCardRow[]): TaskColumns {
  return mapColumns((status) => tasks.filter((task) => task.status === status))
}

export function findTaskColumn(columns: TaskColumns, dndId: string): TaskStatus | null {
  const status = columnStatusFromDndId(dndId)
  if (status) return status
  const taskId = taskIdFromDndId(dndId)
  if (taskId == null) return null
  return TASK_STATUSES.find((s) => columns[s].some((task) => task.id === taskId)) ?? null
}

export function findTask(columns: TaskColumns, taskId: number): TaskCardRow | null {
  for (const status of TASK_STATUSES) {
    const task = columns[status].find((row) => row.id === taskId)
    if (task) return task
  }
  return null
}

export function moveTaskTo(columns: TaskColumns, taskId: number, status: TaskStatus, index: number): TaskColumns {
  const task = findTask(columns, taskId)
  if (!task) return columns
  const next = mapColumns((s) => columns[s].filter((row) => row.id !== taskId))
  const target = [...next[status]]
  target.splice(Math.max(0, Math.min(index, target.length)), 0, task.status === status ? task : { ...task, status })
  return { ...next, [status]: target }
}

export function taskPlacement(columns: TaskColumns, taskId: number): TaskPlacement | null {
  for (const status of TASK_STATUSES) {
    const list = columns[status]
    const index = list.findIndex((task) => task.id === taskId)
    if (index < 0) continue
    return {
      status,
      after_id: index > 0 ? list[index - 1].id : null,
      before_id: index < list.length - 1 ? list[index + 1].id : null,
    }
  }
  return null
}

export function taskPersonName(person: TaskPerson): PersonName {
  return { first_name: person.first_name, last_name: person.last_name, email: person.email ?? "" }
}

export function taskPersonLabel(person: TaskPerson, unnamed: string): string {
  return personDisplayName(taskPersonName(person)) || unnamed
}

export function taskSearchMatches(label: string, query: string): boolean {
  const needle = query.trim().toLowerCase()
  return needle === "" || label.toLowerCase().includes(needle)
}

export const TASK_PERSON_FILTER_ROLES = ["assignee", "author"] as const

export type TaskPersonFilterRole = (typeof TASK_PERSON_FILTER_ROLES)[number]

export type TaskPersonFilter = { role: TaskPersonFilterRole; person: "me" | number | null }

function parseFilterPerson(value: string | null, currentUserId: number | null): TaskPersonFilter["person"] {
  if (value === "me") return "me"
  if (value == null || !/^[1-9]\d*$/.test(value)) return null
  const userId = Number(value)
  return userId === currentUserId ? "me" : userId
}

export function parseTaskPersonFilter(url: string, currentUserId: number | null): TaskPersonFilter {
  const params = new URL(url, "http://localhost").searchParams
  for (const role of TASK_PERSON_FILTER_ROLES) {
    const person = parseFilterPerson(params.get(role), currentUserId)
    if (person != null) return { role, person }
  }
  return { role: "assignee", person: null }
}

export const TASK_SEARCH_MAX_LENGTH = 200
const TASK_SEARCH_MAX_WORDS = 6
const TASK_NUMBER_WORD = /^[1-9]\d{0,8}$/
const TASK_TEXT_WORD = /[\p{Alphabetic}\p{Nd}]/u

export function taskSearchQuery(text: string): string {
  const chars = Array.from(text).slice(0, TASK_SEARCH_MAX_LENGTH)
  const words = chars
    .filter((char) => char !== "\u0000")
    .join("")
    .split(/\s+/u)
  const seen = new Set<string>()
  const kept: string[] = []
  for (const word of words) {
    if (kept.length === TASK_SEARCH_MAX_WORDS) break
    const significant = TASK_NUMBER_WORD.test(word) || (Array.from(word).length >= 2 && TASK_TEXT_WORD.test(word))
    const key = word.toLowerCase()
    if (!significant || seen.has(key)) continue
    seen.add(key)
    kept.push(word)
  }
  return kept.join(" ")
}

export type TaskBoardFilter = TaskPersonFilter & { q: string }

export function parseTaskBoardFilter(url: string, currentUserId: number | null): TaskBoardFilter {
  const q = new URL(url, "http://localhost").searchParams.get("q") ?? ""
  return { ...parseTaskPersonFilter(url, currentUserId), q: taskSearchQuery(q) }
}

export function taskBoardQuery({
  projectId = null,
  filter,
}: {
  projectId?: number | null
  filter: TaskPersonFilter & { q?: string }
}): string {
  const params = new URLSearchParams()
  if (projectId != null) params.set("project_id", String(projectId))
  if (filter.person != null) params.set(filter.role, String(filter.person))
  if (filter.q) params.set("q", filter.q)
  const query = params.toString()
  return query ? `?${query}` : ""
}

export function samePersonFilter(a: TaskPersonFilter, b: TaskPersonFilter): boolean {
  return a.person === b.person && (a.person == null || a.role === b.role)
}

export function filterTasksByPerson(
  tasks: TaskCardRow[],
  filter: TaskPersonFilter,
  currentUserId: number | null,
): TaskCardRow[] {
  if (filter.person == null) return tasks
  const userId = filter.person === "me" ? currentUserId : filter.person
  return tasks.filter((task) => {
    const person = filter.role === "author" ? task.created_by : task.assignee
    return person != null && person.user_id === userId
  })
}

export function taskFilterPeople(
  people: TaskPerson[],
  members: TaskPerson[],
  unnamed: string,
  excludeUserId: number | null,
): TaskPerson[] {
  const byId = new Map<number, TaskPerson>()
  for (const person of [...members, ...people]) {
    if (person.user_id === excludeUserId) continue
    const known = byId.get(person.user_id)
    if (!known || (known.email == null && person.email != null)) byId.set(person.user_id, person)
  }
  const label = (person: TaskPerson) => taskPersonLabel(person, unnamed).toLowerCase()
  return [...byId.values()].sort((a, b) => label(a).localeCompare(label(b)) || a.user_id - b.user_id)
}

export function isTaskOverdue(task: Pick<TaskCardRow, "due_on" | "status">, today: string): boolean {
  return task.due_on != null && task.status !== "done" && task.due_on < today
}

export function formatTaskDue(dueOn: string, locale: string, now: Date = new Date()): string {
  return formatShortDate(`${dueOn}T00:00:00`, locale, now)
}

export function formatTaskCreatedAt(iso: string, locale: string): string {
  const time = new Date(iso).toLocaleTimeString(locale.startsWith("ru") ? "ru-RU" : "en-US", {
    hour: "2-digit",
    minute: "2-digit",
  })
  return `${formatShortDate(iso, locale)}, ${time}`
}

const COMPANY_TASK_PATH = /^\/company\/[^/?#]+\/tasks\/[A-Z]{2,4}-[1-9]\d*(?=[?#]|$)/
const PROJECT_TASK_PATH = /^(\/projects\/\d+\/workspace\/tasks)\/[A-Z]{2,4}-[1-9]\d*(?=[?#]|$)/

export function projectTaskPath(task: Pick<TaskCardRow, "project_id" | "key">): string {
  return `/projects/${task.project_id}/workspace/tasks/${task.key}`
}

export function companyTaskPath(companySlug: string, key: string): string {
  return `/company/${companySlug}/tasks/${key}`
}

export function taskCommentAnchor(commentId: number): string {
  return `comment-${commentId}`
}

export function commentIdFromHash(hash: string): number | null {
  const match = /^#comment-([1-9]\d*)$/.exec(hash)
  return match ? Number(match[1]) : null
}

export function shellNavUrl(fullUrl: string): string {
  const url = fullUrl.split("#", 1)[0]
  const company = COMPANY_TASK_PATH.exec(url)
  if (company) return `/company/tasks${url.slice(company[0].length)}`
  const project = PROJECT_TASK_PATH.exec(url)
  return project ? `${project[1]}${url.slice(project[0].length)}` : url
}

export const TASK_DRAFT_MAX_FILES = 25
export const TASK_DRAFT_MAX_UPLOAD_MB = 500
export const TASK_DRAFT_MAX_UPLOAD_BYTES = TASK_DRAFT_MAX_UPLOAD_MB * 1024 * 1024

export type TaskDraftFields = {
  title: string
  description: string
  status: TaskStatus
  assignee_id: number | null
  due_on: string | null
  agent: TaskAgent
  agent_shot_id: number | null
  asset_kind: TaskAssetKind | null
  asset_id: number | null
  kind: TaskKind
  epic_id: number | null
  project_id: number | null
}

// Пустая форма закрывается молча, а её черновик удаляется: статус, исполнитель, срок и агент без текста и файлов — не содержимое.
export function isTaskDraftFormEmpty(
  fields: Pick<TaskDraftFields, "title" | "description">,
  filesCount: number,
): boolean {
  return fields.title.trim() === "" && fields.description.trim() === "" && filesCount === 0
}

export function taskDraftFormKey(fields: TaskDraftFields, fileIds: readonly number[]): string {
  return JSON.stringify([fields, fileIds])
}

export const TASK_PANEL_WIDTH_STORAGE_KEY = "tasks.panelWidth"
export const TASK_COLUMN_MIN_WIDTH_PX = 256

export function defaultTaskColumnWeights(): number[] {
  return TASK_STATUSES.map(() => 1)
}

export function parseTaskColumnWeights(raw: string | null): number[] {
  if (raw == null) return defaultTaskColumnWeights()
  try {
    const value: unknown = JSON.parse(raw)
    const valid =
      Array.isArray(value) &&
      value.length === TASK_STATUSES.length &&
      value.every((weight): weight is number => typeof weight === "number" && Number.isFinite(weight) && weight > 0)
    return valid ? value : defaultTaskColumnWeights()
  } catch {
    return defaultTaskColumnWeights()
  }
}

export function taskColumnWeightsFromWidths(widths: readonly number[]): number[] {
  const mean = widths.reduce((sum, width) => sum + width, 0) / widths.length
  return widths.map((width) => (mean > 0 ? width / mean : 1))
}

// Веса — пропорционально текущим ширинам (taskColumnWeightsFromWidths); без сдвига возвращает те же веса.
export function resizeTaskColumns(
  weights: number[],
  index: number,
  widths: { left: number; right: number },
  dx: number,
  minWidth: number = TASK_COLUMN_MIN_WIDTH_PX,
): number[] {
  const total = widths.left + widths.right
  if (total <= 2 * minWidth || index < 0 || index + 1 >= weights.length) return weights
  const left = Math.min(total - minWidth, Math.max(minWidth, widths.left + dx))
  if (left === widths.left) return weights
  const pair = weights[index] + weights[index + 1]
  const next = [...weights]
  next[index] = (pair * left) / total
  next[index + 1] = pair - next[index]
  return next
}

// Граница возвращается туда, где стояла бы при равных колонках; соседние границы не двигаются.
export function resetTaskColumnBorder(widths: readonly number[], index: number, minWidth?: number): number[] {
  const weights = taskColumnWeightsFromWidths(widths)
  if (index < 0 || index + 1 >= widths.length) return weights
  const total = widths.reduce((sum, width) => sum + width, 0)
  const before = widths.slice(0, index).reduce((sum, width) => sum + width, 0)
  const target = ((index + 1) * total) / widths.length - before
  const pair = { left: widths[index], right: widths[index + 1] }
  return resizeTaskColumns(weights, index, pair, target - widths[index], minWidth)
}

function fractionDigits(value: string): number {
  const dot = value.indexOf(".")
  return dot < 0 ? 0 : value.length - dot - 1
}

function scaledDecimal(value: string, scale: number): bigint {
  const [whole, fraction = ""] = value.replace(/^[-+]/, "").split(".")
  const digits = BigInt(whole + fraction.padEnd(scale, "0"))
  return value.startsWith("-") ? -digits : digits
}

// Позиция приходит строкой PG numeric: у середин между соседями знаков больше, чем держит double.
export function compareTaskPositions(a: string, b: string): number {
  const scale = Math.max(fractionDigits(a), fractionDigits(b))
  const diff = scaledDecimal(a, scale) - scaledDecimal(b, scale)
  return diff < BigInt(0) ? -1 : diff > BigInt(0) ? 1 : 0
}

function compareTaskRows(a: TaskCardRow, b: TaskCardRow): number {
  return compareTaskPositions(a.position, b.position) || a.id - b.id
}

function cardFields(row: TaskCardRow): TaskCardRow {
  return {
    id: row.id,
    project_id: row.project_id,
    key: row.key,
    number: row.number,
    title: row.title,
    status: row.status,
    position: row.position,
    updated_at: row.updated_at,
    due_on: row.due_on,
    assignee: row.assignee,
    created_by: row.created_by,
    comments_count: row.comments_count,
    files_count: row.files_count,
    access: row.access,
    asset_kind: row.asset_kind,
    asset: row.asset,
    kind: row.kind,
    epic: row.epic,
    child_tasks_count: row.child_tasks_count,
    child_tasks_done_count: row.child_tasks_done_count,
    // Только у задач из проверки (docs/shot_review.md): у остальных карточек поля нет.
    ...(row.review_kind ? { review_kind: row.review_kind } : {}),
  }
}

export function taskCardRow(row: TaskCardRow): TaskCardRow {
  return row.search ? { ...cardFields(row), search: row.search } : cardFields(row)
}

// null — карточке не место на доске под этим запросом (убрать, если стоит; не вставлять).
export function taskCardUnderSearch(
  row: TaskCardRow,
  current: TaskCardRow | null,
  search: TaskBoardSearch | null,
): TaskCardRow | null {
  const card = cardFields(row)
  if (search == null) return card
  if (row.search?.q === search.q) return row.search.match ? { ...card, search: row.search } : null
  if (!current) return null
  return current.search ? { ...card, search: current.search } : card
}

function sameAsset(a: TaskCardRow["asset"], b: TaskCardRow["asset"]): boolean {
  if (a == null || b == null) return a === b
  return a.id === b.id && a.label === b.label && (a.thumb_url ?? null) === (b.thumb_url ?? null)
}

function samePerson(a: TaskPerson | null, b: TaskPerson | null): boolean {
  if (a == null || b == null) return a === b
  return a.user_id === b.user_id && a.email === b.email && a.first_name === b.first_name && a.last_name === b.last_name
}

export function sameTaskCard(a: TaskCardRow, b: TaskCardRow): boolean {
  return (
    a.id === b.id &&
    a.project_id === b.project_id &&
    a.key === b.key &&
    a.number === b.number &&
    a.title === b.title &&
    a.status === b.status &&
    a.position === b.position &&
    a.updated_at === b.updated_at &&
    a.due_on === b.due_on &&
    a.comments_count === b.comments_count &&
    a.files_count === b.files_count &&
    a.access === b.access &&
    a.asset_kind === b.asset_kind &&
    sameAsset(a.asset, b.asset) &&
    a.kind === b.kind &&
    a.epic?.id === b.epic?.id &&
    a.child_tasks_count === b.child_tasks_count &&
    a.child_tasks_done_count === b.child_tasks_done_count &&
    samePerson(a.assignee, b.assignee) &&
    a.assignee?.on_team === b.assignee?.on_team &&
    samePerson(a.created_by, b.created_by) &&
    a.search?.match === b.search?.match &&
    a.search?.snippet === b.search?.snippet
  )
}

// Ответ, пришедший позже более свежего (updated_at старше), строку не откатывает; без изменений — тот же массив.
// Порядок держится внутри статуса: порции колонок дописываются в конец, общего порядка по позиции у списка нет.
export function upsertTaskRow(rows: TaskCardRow[], row: TaskCardRow): TaskCardRow[] {
  const index = rows.findIndex((candidate) => candidate.id === row.id)
  const current = index < 0 ? null : rows[index]
  if (current && (current.updated_at > row.updated_at || sameTaskCard(current, row))) return rows
  if (current && current.status === row.status && current.position === row.position) {
    return rows.map((candidate, i) => (i === index ? row : candidate))
  }
  const rest = current ? rows.filter((candidate) => candidate.id !== row.id) : rows
  const at = rest.findIndex((candidate) => candidate.status === row.status && compareTaskRows(row, candidate) < 0)
  return at < 0 ? [...rest, row] : [...rest.slice(0, at), row, ...rest.slice(at)]
}

export function removeTaskRow(rows: TaskCardRow[], taskId: number): TaskCardRow[] {
  return rows.some((row) => row.id === taskId) ? rows.filter((row) => row.id !== taskId) : rows
}

export function taskAfterCursor(row: Pick<TaskCardRow, "position" | "id">, cursor: TaskColumnCursor | null): boolean {
  return cursor != null && (compareTaskPositions(row.position, cursor.position) || row.id - cursor.id) > 0
}

export function sameTaskCursor(a: TaskColumnCursor | null, b: TaskColumnCursor | null): boolean {
  return a == null || b == null ? a === b : a.id === b.id && a.position === b.position
}

export type TaskBoardRows = { tasks: TaskCardRow[]; columns: TaskColumnsState | null }

// Карточка за курсором колонки ждёт своей порции: иначе она встала бы в конец загруженного.
// extendCursor — карточка встала сразу за последней загруженной, между ними ничего нет: курсор переезжает на неё.
export function placeTaskCard(board: TaskBoardRows, row: TaskCardRow, extendCursor = false): TaskBoardRows {
  const { tasks, columns } = board
  const column = columns?.[row.status]
  if (!columns || !column || !taskAfterCursor(row, column.cursor)) {
    const next = upsertTaskRow(tasks, row)
    return next === tasks ? board : { tasks: next, columns }
  }
  const current = tasks.find((task) => task.id === row.id)
  if (current && current.updated_at > row.updated_at) return board
  if (extendCursor) {
    return {
      tasks: upsertTaskRow(tasks, row),
      columns: { ...columns, [row.status]: { ...column, cursor: { position: row.position, id: row.id } } },
    }
  }
  if (!current) return board
  return {
    tasks: removeTaskRow(tasks, row.id),
    columns: { ...columns, [row.status]: { ...column, remaining: column.remaining + 1 } },
  }
}

// Порция ложится, только если колонка кончается там же, откуда её запросили.
export function appendTaskColumnPage(
  board: TaskBoardRows,
  status: TaskStatus,
  page: TaskColumnPage,
  after: TaskColumnCursor,
): TaskBoardRows {
  const { tasks, columns } = board
  if (!columns || !sameTaskCursor(columns[status].cursor, after)) return board
  return {
    tasks: page.tasks.reduce((rows, row) => upsertTaskRow(rows, row), tasks),
    columns: { ...columns, [status]: page.column },
  }
}

export function changeTaskCommentsCount(rows: TaskCardRow[], taskId: number, delta: number): TaskCardRow[] {
  const index = rows.findIndex((row) => row.id === taskId)
  if (index < 0) return rows
  const count = Math.max(0, rows[index].comments_count + delta)
  if (count === rows[index].comments_count) return rows
  return rows.map((row, i) => (i === index ? { ...row, comments_count: count } : row))
}

function remainingShifted(columns: TaskColumnsState, status: TaskStatus, delta: number): TaskColumnsState {
  const column = columns[status]
  return { ...columns, [status]: { ...column, remaining: Math.max(0, column.remaining + delta) } }
}

// Незагруженная открытая задача числится в «осталось» своей колонки, если лежит за курсором и проходит фильтр:
// при смене статуса, удалении или переназначении этот учёт переносится вслед за ней.
export function recountUnloadedTask(
  before: TaskBoardRows,
  after: TaskBoardRows,
  previous: TaskCardRow,
  next: TaskCardRow | null,
  matches: (row: TaskCardRow) => boolean,
): TaskBoardRows {
  if (!before.columns || !after.columns || before.tasks.some((task) => task.id === previous.id)) return after
  const counted = (row: TaskCardRow, columns: TaskColumnsState) =>
    taskAfterCursor(row, columns[row.status].cursor) && matches(row)
  const wasCounted = counted(previous, before.columns)
  const isCounted = next != null && !after.tasks.some((task) => task.id === next.id) && counted(next, after.columns)
  if (wasCounted === isCounted && (!wasCounted || previous.status === next?.status)) return after
  let columns = after.columns
  if (wasCounted) columns = remainingShifted(columns, previous.status, -1)
  if (isCounted && next) columns = remainingShifted(columns, next.status, 1)
  return { ...after, columns }
}
